import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import axios from '../../utils/axios';
import { createAuthedThunk } from '../authedThunk';

const contactsInitialState = {
  fetching: null,
  fetchingPagination: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  contacts: [],
  emptyList: null,
  lastSeen: null,
  // Whether the last page came back full, i.e. another may follow. Tracked
  // here rather than derived from the list length, which a locally added or
  // removed contact knocks off the page-size boundary.
  hasMore: false,
  // Which creator the cached list was fetched for (null = unscoped). Consumers
  // that need the unscoped list refetch when this is set - see ContactAutocomplete.
  createdBy: null,
};

const fetchOneInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  contact: null,
};

const searchInitialState = {
  searching: null,
  searchSuccessful: null,
  searchFailed: null,
  error: null,
  errorText: null,
  results: [],
};

const fetchDataInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  data: null,
};

const mutationInitialState = {
  processing: null,
  done: null,
  failed: null,
  error: null,
  errorText: null,
  data: null,
};

const initialState = {
  contacts: { ...contactsInitialState },
  contactById: { ...fetchOneInitialState },
  contactSearch: { ...searchInitialState },
  createContact: { ...mutationInitialState },
  updateContact: { ...mutationInitialState },
  deleteContact: { ...mutationInitialState },
  linkedinSync: { ...mutationInitialState },
  linkedinScrape: { ...mutationInitialState },
  scrapeCompanyEmployees: { ...mutationInitialState },
  crmLink: { ...mutationInitialState },
  checkDuplicates: { ...mutationInitialState },
  contactDealResearch: { ...fetchDataInitialState },
  contactDeals: { ...fetchDataInitialState },
};

// Shared auth wrapper - see `redux/authedThunk`. Was a byte-identical
// local copy in each of eleven slices.
const thunk = createAuthedThunk;

/**
 * Fetches contacts, optionally scoped to the ones a given user created.
 *
 * Both endpoints scope non-admin callers to their own records server-side, so
 * the `createdBy` branch exists purely for org admins browsing someone else's
 * contacts - see `isAdminUser`. Without `createdBy` we stay on v1, which keeps
 * every existing caller on the endpoint it has always used and returns the
 * whole org for admins. v2 takes the same paging inputs, just in the body.
 *
 * @param {Object} [arg]
 * @param {string} [arg.companyId] - Limit to one company's contacts.
 * @param {string} [arg.lastSeen] - Pagination cursor (last contact id seen).
 * @param {number} [arg.limit] - Page size.
 * @param {string} [arg.createdBy] - User id to scope to. Org admins only;
 *   ignored (forced to the caller's own id) for everyone else.
 */
export const fetchContactsThunk = thunk(
  'contacts/fetchContacts',
  async (token, { companyId, lastSeen, limit, createdBy } = {}) => {
    const r = await axios.post(
      '/us/contacts/v2/list',
      { companyId, lastSeen, limit, createdBy },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch contacts');
  },
);

export const fetchContactByIdThunk = thunk(
  'contacts/fetchContactById',
  async (token, { contactId }) => {
    const r = await axios.get(`/us/contacts/${contactId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch contact');
  },
);

/**
 * Searches contacts, optionally scoped to one creator - same admin-only
 * `createdBy` rules as `fetchContactsThunk`.
 *
 * The v2 request body carries only `keywords` and `createdBy`; it has no
 * `companyId` equivalent, so callers that need both must stay on v1. Creator
 * scoping on the search path (v1 and v2 alike) also relies on the search index
 * carrying a `created_by` field per record, which rolls out separately from the
 * Firestore-backed list endpoints.
 */
export const searchContactsThunk = thunk(
  'contacts/searchContacts',
  async (token, { keywords, companyId, createdBy } = {}) => {
    const r = await axios.post(
      '/us/contacts/v2/search',
      { keywords, createdBy },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to search contacts');
  },
);

export const createContactThunk = thunk(
  'contacts/createContact',
  async (token, payload) => {
    const r = await axios.post('/us/contacts', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status >= 200 && r.status < 300) return r.data;
    throw new Error('Failed to create contact');
  },
);

export const updateContactThunk = thunk(
  'contacts/updateContact',
  async (token, { contactId, payload }) => {
    const r = await axios.put(`/us/contacts/${contactId}`, payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status >= 200 && r.status < 300) return r.data;
    throw new Error('Failed to update contact');
  },
);

export const deleteContactThunk = thunk(
  'contacts/deleteContact',
  async (token, { contactId }) => {
    const r = await axios.delete(`/us/contacts/${contactId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to delete contact');
  },
);

export const syncContactLinkedinThunk = thunk(
  'contacts/syncContactLinkedin',
  async (token, { contactId }) => {
    const r = await axios.put(`/us/contacts/${contactId}/linkedin/sync`, null, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to sync contact linkedin data');
  },
);

export const scrapeContactLinkedinThunk = thunk(
  'contacts/scrapeContactLinkedin',
  async (token, { url }) => {
    const r = await axios.post(
      '/us/contacts/linkedin/scrape',
      { url },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to scrape linkedin profile');
  },
);

export const scrapeCompanyEmployeesThunk = thunk(
  'contacts/scrapeCompanyEmployees',
  async (token, payload) => {
    const r = await axios.post(
      '/us/contacts/scrape/company/employees',
      payload,
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to scrape company employees');
  },
);

export const updateContactCrmLinkThunk = thunk(
  'contacts/updateContactCrmLink',
  async (token, { contactId, dealId, action, crmId }) => {
    const r = await axios.put(
      `/us/contacts/${contactId}/deals/${dealId}/crm-link`,
      { action, crmId },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to update contact CRM link');
  },
);

export const checkContactDuplicatesThunk = thunk(
  'contacts/checkDuplicates',
  async (token, payload) => {
    const r = await axios.post('/us/contacts/check-duplicates', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to check contact duplicates');
  },
);

export const fetchContactDealsThunk = thunk(
  'contacts/fetchContactDeals',
  async (token, { contactId }) => {
    const r = await axios.get(`/us/contacts/${contactId}/deals`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch contact deals');
  },
);

export const fetchContactDealResearchThunk = thunk(
  'contacts/fetchContactDealResearch',
  async (token, { contactId, dealId, generateNew, topicOfInterest }) => {
    const r = await axios.get(
      `/us/contacts/${contactId}/deals/${dealId}/research`,
      {
        params: { 'request-id': uuidv4(), generateNew, topicOfInterest },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch contact research data');
  },
);

const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    resetContactSearch: (state) => {
      state.contactSearch = { ...searchInitialState };
    },
    setContacts: (state, action) => {
      state.contacts.contacts = action.payload;
    },
    clearContactById: (state) => {
      state.contactById = { ...fetchOneInitialState };
      state.contactDeals = { ...fetchDataInitialState };
    },
  },
  extraReducers: (builder) => {
    const addM = (t, k) =>
      builder
        .addCase(t.pending, (s) => {
          s[k].processing = true;
          s[k].done = false;
          s[k].failed = false;
        })
        .addCase(t.fulfilled, (s, a) => {
          s[k].processing = false;
          s[k].done = true;
          s[k].failed = false;
          s[k].data = a.payload;
        })
        .addCase(t.rejected, (s, a) => {
          s[k].processing = false;
          s[k].done = false;
          s[k].failed = true;
          s[k].error = a.payload;
        });

    builder
      .addCase(fetchContactsThunk.pending, (s, a) => {
        if (a.meta.arg?.lastSeen) {
          s.contacts.fetchingPagination = true;
        } else {
          s.contacts.fetching = true;
        }
        s.contacts.fetchingSuccessful = false;
        s.contacts.fetchingFailed = false;
      })
      .addCase(fetchContactsThunk.fulfilled, (s, a) => {
        s.contacts.fetching = false;
        s.contacts.fetchingPagination = false;
        s.contacts.fetchingSuccessful = true;
        s.contacts.fetchingFailed = false;
        const list = Array.isArray(a.payload)
          ? a.payload
          : a.payload?.contacts || [];
        if (a.meta.arg?.lastSeen) {
          s.contacts.contacts = [...s.contacts.contacts, ...list];
        } else {
          s.contacts.contacts = list;
        }
        s.contacts.emptyList = list.length === 0;
        s.contacts.hasMore =
          Boolean(a.meta.arg?.limit) && list.length >= a.meta.arg.limit;
        s.contacts.lastSeen = list.length ? list[list.length - 1]?.id : null;
        s.contacts.createdBy = a.meta.arg?.createdBy || null;
      })
      .addCase(fetchContactsThunk.rejected, (s, a) => {
        s.contacts.fetching = false;
        s.contacts.fetchingPagination = false;
        s.contacts.fetchingSuccessful = false;
        s.contacts.fetchingFailed = true;
        s.contacts.error = a.payload;
      });

    builder
      .addCase(fetchContactByIdThunk.pending, (s) => {
        s.contactById.fetching = true;
        s.contactById.fetchingSuccessful = false;
        s.contactById.fetchingFailed = false;
      })
      .addCase(fetchContactByIdThunk.fulfilled, (s, a) => {
        s.contactById.fetching = false;
        s.contactById.fetchingSuccessful = true;
        s.contactById.fetchingFailed = false;
        s.contactById.contact = a.payload;
      })
      .addCase(fetchContactByIdThunk.rejected, (s, a) => {
        s.contactById.fetching = false;
        s.contactById.fetchingSuccessful = false;
        s.contactById.fetchingFailed = true;
        s.contactById.error = a.payload;
      });

    builder
      .addCase(searchContactsThunk.pending, (s) => {
        s.contactSearch.searching = true;
        s.contactSearch.searchSuccessful = false;
        s.contactSearch.searchFailed = false;
      })
      .addCase(searchContactsThunk.fulfilled, (s, a) => {
        s.contactSearch.searching = false;
        s.contactSearch.searchSuccessful = true;
        s.contactSearch.searchFailed = false;
        s.contactSearch.results = a.payload;
      })
      .addCase(searchContactsThunk.rejected, (s, a) => {
        s.contactSearch.searching = false;
        s.contactSearch.searchSuccessful = false;
        s.contactSearch.searchFailed = true;
        s.contactSearch.error = a.payload;
      });

    addM(createContactThunk, 'createContact');
    addM(updateContactThunk, 'updateContact');
    addM(deleteContactThunk, 'deleteContact');
    addM(syncContactLinkedinThunk, 'linkedinSync');
    addM(scrapeContactLinkedinThunk, 'linkedinScrape');
    addM(scrapeCompanyEmployeesThunk, 'scrapeCompanyEmployees');
    addM(updateContactCrmLinkThunk, 'crmLink');
    addM(checkContactDuplicatesThunk, 'checkDuplicates');

    builder
      .addCase(fetchContactDealsThunk.pending, (s) => {
        s.contactDeals.fetching = true;
        s.contactDeals.fetchingSuccessful = false;
        s.contactDeals.fetchingFailed = false;
      })
      .addCase(fetchContactDealsThunk.fulfilled, (s, a) => {
        s.contactDeals.fetching = false;
        s.contactDeals.fetchingSuccessful = true;
        s.contactDeals.fetchingFailed = false;
        s.contactDeals.data = a.payload;
      })
      .addCase(fetchContactDealsThunk.rejected, (s, a) => {
        s.contactDeals.fetching = false;
        s.contactDeals.fetchingSuccessful = false;
        s.contactDeals.fetchingFailed = true;
        s.contactDeals.error = a.payload;
      });

    builder
      .addCase(fetchContactDealResearchThunk.pending, (s) => {
        s.contactDealResearch.fetching = true;
        s.contactDealResearch.fetchingSuccessful = false;
        s.contactDealResearch.fetchingFailed = false;
      })
      .addCase(fetchContactDealResearchThunk.fulfilled, (s, a) => {
        s.contactDealResearch.fetching = false;
        s.contactDealResearch.fetchingSuccessful = true;
        s.contactDealResearch.fetchingFailed = false;
        s.contactDealResearch.data = a.payload;
      })
      .addCase(fetchContactDealResearchThunk.rejected, (s, a) => {
        s.contactDealResearch.fetching = false;
        s.contactDealResearch.fetchingSuccessful = false;
        s.contactDealResearch.fetchingFailed = true;
        s.contactDealResearch.error = a.payload;
      });
  },
});

export const {
  resetContactSearch,
  setContacts,
  clearContactById,
} = contactsSlice.actions;

export default contactsSlice.reducer;
